import { BrowserSupport } from '@tableau/browser-support';
import React from 'react';
import _ from 'underscore';

import { ServerUrls } from '~commonInfra/url/ServerUrls';

import { Messages } from '../../../codegen/Localize';
import { LayoutType } from '../../state/LayoutType';
import { ConnectedNavPanel } from './ConnectedNavPanel';
import { NavStyles } from './NavigationStyles';
import { NavPanelButton } from './NavPanelButton';

export const MAX_RESIZE_EVENTS_PER_SECOND = 5;
export const RESIZE_THROTTLE_INTERVAL = 1000 / MAX_RESIZE_EVENTS_PER_SECOND;

export type LeftNavVisibility = 'expanded' | 'minimized';

export interface LeftNavState {
  readonly visibility: LeftNavVisibility;
  readonly userSetVisibility: boolean;
  readonly userVisiblityChoice: LeftNavVisibility;
  readonly visibleVerticalScroll: boolean;
}

export interface LeftNavProps {
  readonly isInVizViewerArea: boolean;
  readonly layoutType: LayoutType;
}

export class LeftNav extends React.PureComponent<LeftNavProps, LeftNavState> {

  private parentNavElement: HTMLDivElement = null;

  public constructor(props: LeftNavProps) {
    super(props);
    // this will get overridden by getDerivedStateFromProps before first render
    // But I need to add so tests play nice because shallow render doesn't call getDerivedStateFromProps
    this.state = {
      visibility: LeftNav.getVisibilityFromLayoutType(props.layoutType),
      userSetVisibility: false,
      userVisiblityChoice: 'expanded',
      visibleVerticalScroll: false,
    };
  }

  // Takes the user choice if the user has set it.
  static getDerivedStateFromProps(nextProps: LeftNavProps, prevState: LeftNavState): Partial<LeftNavState>{
    const visibility = prevState.userSetVisibility ? prevState.userVisiblityChoice : LeftNav.getVisibilityFromLayoutType(nextProps.layoutType);
    return {
      visibility
    };
  }

  private handleRef = (ref: HTMLDivElement): void => {
    this.parentNavElement = ref;
    this.updateVerticalScrollState();
  };

  private updateVerticalScrollState() {
    if (this.parentNavElement) {
      const showingScrollbar = this.parentNavElement.clientHeight < this.parentNavElement.scrollHeight;
      this.setState({visibleVerticalScroll: showingScrollbar});
    }
  }

  private onWindowResize = () => {
    this.updateVerticalScrollState();
  };
  private throttledOnWindowResize = _.throttle(this.onWindowResize, RESIZE_THROTTLE_INTERVAL);

  public componentDidMount() {
    if (BrowserSupport.IsWindows()) {
      window.addEventListener('resize', this.throttledOnWindowResize);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.throttledOnWindowResize);
  }

  public render(): JSX.Element {
    if (this.props.isInVizViewerArea) {
      return null;
    }

    return (
      <div className={NavStyles.getLeftNavClassNames(this.state.visibility, this.state.visibleVerticalScroll)} ref={this.handleRef}>
        <NavPanelButton
          panelState={this.state.visibility}
          visibleVerticalScroll={this.state.visibleVerticalScroll}
          toggleNavPanel={this.toggleLeftNav}
          panelOpenMessage={Messages.leftNavToggleButtonCollapse()}
        />
        <ConnectedNavPanel
          navPanelDisplayMode={this.state.visibility === 'expanded' ? 'iconAndText' : 'icon'}
          serverUrls={ServerUrls.getInstance()}
          onSitePickerClick={this.openPanel}
        />
      </div>
    );
  }

  static getVisibilityFromLayoutType(layoutType: LayoutType): LeftNavVisibility {
    return layoutType === 'desktop' ? 'expanded' : 'minimized';
  }

  private toggleLeftNav = () => {
    this.setState((oldState) => {
      const toggledVisibility = (oldState.visibility === 'expanded' ? 'minimized' : 'expanded');
      return { userSetVisibility: true, userVisiblityChoice: toggledVisibility };
    });
  };

  private openPanel = () => {
    this.setState({userVisiblityChoice: 'expanded', userSetVisibility: true});
  };
}
